import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';

// DESIGN.md section 6: primary is the brand green, secondary is white with a
// thin border, ghost for tertiary. 8px radius, medium weight, 40px targets.
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-[14px] font-medium ring-offset-background transition-[background-color,border-color,color,transform] duration-fast ease-standard focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // The primary action answers the hand: a sheen sweeps across on hover
        // (.cb-shine, App.css: a pseudo-element, so the button's children are
        // untouched) and it presses 1px down. Transform only, both still under
        // reduced motion. `relative` holds the sheen inside the button; a
        // className that positions the button still wins.
        default: 'cb-shine relative bg-primary text-primary-foreground hover:bg-brand-700 active:translate-y-px motion-reduce:active:translate-y-0 dark:hover:bg-brand-300',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-rose-700',
        // A secondary action must read as a control at rest: the input edge
        // (a step firmer than a card's) and full ink, not a hairline and grey.
        outline: 'border border-input bg-surface text-foreground hover:border-border-strong hover:bg-surface-subtle',
        secondary: 'bg-ink-100 text-ink-900 hover:bg-ink-200 dark:bg-white/10 dark:text-ink-100 dark:hover:bg-white/15',
        ghost: 'text-ink-700 hover:bg-accent hover:text-foreground dark:text-ink-300',
        link: 'text-primary underline-offset-4 hover:underline',
        soft: 'bg-primary-soft text-primary hover:bg-brand-100 dark:hover:bg-brand-900',
      },
      size: {
        default: 'h-10 px-4',
        sm: 'h-8 rounded-md px-3 text-[13px]',
        lg: 'h-[46px] rounded-md px-[22px] text-[15px]',
        icon: 'size-10',
        'icon-sm': 'size-8',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  }
);

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';
  return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
});
Button.displayName = 'Button';

export { Button, buttonVariants };
